<template>
  <div class="um-audio-gallery">
    <nav class="nav">
      <a 
        href="#" 
        class="button" 
        :class="{'button--date': true, 'button--active': 'timestamp' === sortProp}"
        @click.prevent="sortProp = 'timestamp'">
        Date
      </a>
      
      <a 
        v-for="sp in sortProps" :key="sp.prop"
        href="#" 
        class="button" 
        :class="{[`button--${sp.em}`]: true, 'button--active': sp.prop === sortProp}" 
        @click.prevent="sortProp = sp.prop">
        {{sp.label}}
      </a>
    </nav>
    <p v-if="sortProp && sortProp !== 'timestamp'" class="audio-gallery__sort-description">
      Below is a list of sounds recorded when people felt <span class="audio-gallery__sort-prop-label">{{sortProps.find(p => p.prop === sortProp).label}}</span>
    </p>
    <transition name="fade" v-if="sortedItems" appear>
      <div class="audio-gallery">
        <div v-for="(item, idx) in sortedItems" class="audio-gallery-item" :key="`${item.id}-${idx}`">
          <um-audio-player class="audio-player" :item="item" @play="resetCurrentAudio($event)"/>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-if="item.image" @click.prevent="currentItem = item" style="cursor:pointer">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path d="M9.4 10.5l4.77-8.26C13.47 2.09 12.75 2 12 2c-2.4 0-4.6.85-6.32 2.25l3.66 6.35.06-.1zM21.54 9c-.92-2.92-3.15-5.26-6-6.34L11.88 9h9.66zm.26 1h-7.49l.29.5 4.76 8.25C21 16.97 22 14.61 22 12c0-.69-.07-1.35-.2-2zM8.54 12l-3.9-6.75C3.01 7.03 2 9.39 2 12c0 .69.07 1.35.2 2h7.49l-1.15-2zm-6.08 3c.92 2.92 3.15 5.26 6 6.34L12.12 15H2.46zm11.27 0l-3.9 6.76c.7.15 1.42.24 2.17.24 2.4 0 4.6-.85 6.32-2.25l-3.66-6.35-.93 1.6z" :fill="currentItem === item ? '#009429' : '#7E90A7'"/>
          </svg>
        </div>
      </div>
    </transition>
    <div class="loading-container" v-else>
      <app-loading />
    </div>
    <infinite-loading @infinite="infiniteHandler"></infinite-loading>
    <transition name="fade" v-if="currentItem" mode="out-in" appear>
      <div class="modal" @click="currentItem = null">
        <div class="modal-bg" @click="currentItem = null"></div>
        <span class="modal-arrow" @click="currentItem = null">
          &#8601;
        </span>
        <div class="modal-content">
          <img :src="imageURL(currentItem.image.path,1200)" class="modal-content__image"/>
        </div>
        <div class="modal-footer">
          {{currentItem | text}}
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import sortBy from 'lodash/sortBy'
  import compact  from 'lodash/compact'
  import InfiniteLoading from 'vue-infinite-loading'
  import AppLoading from './AppLoading.vue'
  import UMAudioPlayer from './AudioPlayer.vue'

  const shortestTime = (timestamp) => {
    const parts = (new Date(timestamp)).toGMTString().split(' ')
    return `${parts[2]} ${parts[3]}`
  }

  export default {
    name: 'UMAudioGallery',
    components: {
      'app-loading': AppLoading,
      'um-audio-player': UMAudioPlayer,
      InfiniteLoading
    },
    props: {
      api: {
        type: String,
        required: true
      },
      cdn: {
        type: String,
        required: true
      },
      limit: {
        type: Number,
        default: 20
      }
    },
    computed: {
      sortedItems () {
        const {items, sortProp} = this
        if (!items) return

        const sorted = 
          sortProp
            ? sortBy(items, i => i[sortProp] || i.data[sortProp])
                .filter(i => sortProp === 'timestamp' || (i[sortProp] || i.data[sortProp]) > 3)
            : items

        return sorted.filter(({image}) => !!image).reverse()
      }
    },
    data () {
      return {
        loading: false,
        currentAudio: null,
        page: 1,
        counts: null,
        currentItem: null,
        items: null,
        error: null,
        sortProp: 'timestamp',
        sortProps: [
          {em: 'negative', prop: "right_now_i_feel_anxious", label: 'Anxious'},
          {em: 'negative', prop: "right_now_i_feel_stressed", label: 'Stressed'},
          {em: 'negative', prop: "right_now_i_feel_down", label: 'Down'},
          {em: 'negative', prop: "right_now_i_feel_lonely", label: 'Lonely'},
          {em: 'negative', prop: "right_now_i_feel_tired", label: 'Tired'},
          {em: 'positive', prop: "right_now_i_feel_confident", label: 'Confident'},
          {em: 'positive', prop: "right_now_i_feel_relaxed", label: 'Relaxed'},
          {em: 'positive', prop: "right_now_i_feel_happy", label: 'Happy'},
          {em: 'positive', prop: "right_now_i_feel_connected_with_other_people", label: 'Connected'},
          {em: 'positive', prop: "right_now_i_feel_energetic", label: 'Energetic'}
        ]
      }
    },
    methods: {
      resetCurrentAudio(item) {
        const { currentAudio } = this
        if (currentAudio && currentAudio.$refs.audio.src !== item.$refs.audio.src) {
          currentAudio.pause()
        }
        this.currentAudio = item
      },
      imageURL(path, width = 300) {
        const parts = path.split('/')
        const file = parts.pop()
        return `${this.cdn}/assets${parts.join('/')}/${width}/${file}`
      },
      async infiniteHandler($state) {
        if (this.loading) {
          return
        }

        this.loading = true
        try {
          const count = await this.loadNext()
        
          if (count === 0) {
            $state.complete()
            return
          }

          this.page += 1

          if (this.counts && this.page >= Math.ceil(this.counts / this.limit)) {
            $state.complete()
          } else {
            $state.loaded()
          }

          this.loading = false
        } catch (err) {
          this.loading = false
          $state.complete()
        }
      },
      loadNext(){
        const { page, api, limit } = this

        return fetch(api + '/audio?limit=' + limit + '&page=' + page)
        .then(resp => resp.json())
        .then(({count, data}) => {
          this.counts = count
          const oldItems = this.items || []
          this.items = oldItems.concat(data)
          return data.length
        })
        .catch(error => {
          this.error = error.message
          throw error
        })
      }
    },
    filters: {
      text(item) {
        const {title,location,timestamp} = item
        const locationName = location ? (location.display_name || location.full_name || location.name) : null
        return compact([shortestTime(timestamp), title, locationName]).join(' | ')
      }
    }
  }
</script>

<style>
@import "./styles/button.css";
@import "./styles/fade-transition.css";
@import "./styles/loading-container.css";
@import "./styles/modal.css";
@import "./styles/nav.css";
@import "./styles/audio-gallery.css";
@import "./styles/audio-player.css";
.audio-gallery__sort-description {
  text-align: center;
}
.audio-gallery__sort-prop-label {
  text-transform: lowercase;
}
</style>
